import { FC, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { useLocation } from 'react-router';
import {
  Box,
  Stack,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { DatePicker, DateRange } from '@mui/x-date-pickers-pro';
import { format } from 'date-fns';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { DateInputFormat } from '@shared/helpers/date';

import { StoryTagAutocomplete } from '@modules/Stories/components/StoryTagAutocomplete';

import { MeetingStatusSelect } from '../components/MeetingStatusSelect';


export type Filters = keyof Meetings.GetListParams;

export interface Props {
  filters?: Meetings.GetListParams;
  setters?: {
    [K in Filters]?: (value: Meetings.GetListParams[K]) => void;
  };
  disabledFilters?: Filters[];
}

export const MeetingsFilters: FC<Props> = ({
  filters,
  setters,
  disabledFilters = [],
}) => {
  const dictionary = useDictionary();
  const { pathname } = useLocation();

  const [_search, _setSearch] = useState(filters?.search);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  useDebounce(() => {
    setters?.search?.(_search);
  }, 500, [_search]);

  const dateRangeValue = useMemo(() => {
    return [
      filters?.dateGte ? new Date(filters.dateGte + 'T00:00:00.000') : null,
      filters?.dateLte ? new Date(filters.dateLte + 'T00:00:00.000') : null,
    ] as DateRange<Date>;
  }, [filters]);

  const dateRangeRestrictions = useMemo(() => {
    let maxDate;
    let minDate;
    if (pathname.includes('upcoming')) {
      minDate = new Date();
    } else if (pathname.includes('past')) {
      maxDate = new Date();
    }

    return {
      min: minDate,
      max: maxDate,
    };
  }, [pathname]);

  return (
    <Box width="100%" p={{ xs: 0, sm: 2 }}>
      <Stack direction="row" width="100%">
        <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap={{ xs: 'nowrap', sm: 'wrap' }} alignItems="center" gap={2} width="100%">
          {!disabledFilters.includes('search') && (
            <TextField
              fullWidth={isMobile}
              value={_search || ''}
              label={dictionary.filters.fieldSearch}
              onChange={e => _setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
            />
          )}

          {!disabledFilters.includes('status') && (
            <MeetingStatusSelect
              value={filters?.status || ''}
              size="small"
              fullWidth={isMobile}
              sx={{
                minWidth: 170,
                width: isMobile ? '100%' : 170,
              }}
              label=""
              placeholder={dictionary.forms.meeting.allMeetingStatuses}
              onChange={(event) => {
                const selectedValue = event.target.value as Meetings.MeetingStatus;
                setters?.status?.(selectedValue);
              }}
            />
          )}

          {!disabledFilters.includes('tags') && (
            <StoryTagAutocomplete
              sx={{ minWidth: 250, width: isMobile ? '100%' : 250 }}
              size="small"
              TextFieldProps={{
                placeholder: dictionary.forms.story.tagsFilterPlaceholder,
              }}
              fullWidth={isMobile}
              label=""
              value={filters?.tags || []}
              onChange={(value) => setters?.tags?.(value)}
            />
          )}

          {!disabledFilters.includes('dateGte') && (
            <DatePicker
              value={dateRangeValue[0]}
              openTo="year"
              views={['year', 'month', 'day']}
              minDate={dateRangeRestrictions.min}
              maxDate={dateRangeRestrictions.max}
              format={DateInputFormat}
              onChange={(date) => {
                const formattedDate = date ? format(date, 'yyyy-MM-dd') : undefined;
                setters?.dateGte?.(formattedDate);
              }}
              slotProps={{
                textField: {
                  label: 'From date',
                  size: 'small',
                  sx: { minWidth: 160, width: isMobile ? '100%' : 160 },
                  fullWidth: isMobile,
                },
              }}
            />
          )}
          {!disabledFilters.includes('dateLte') && (
            <DatePicker
              value={dateRangeValue[1]}
              openTo="year"
              views={['year', 'month', 'day']}
              minDate={dateRangeRestrictions.min}
              maxDate={dateRangeRestrictions.max}
              format={DateInputFormat}
              onChange={(date) => {
                const formattedDate = date ? format(date, 'yyyy-MM-dd') : undefined;
                setters?.dateLte?.(formattedDate);
              }}
              slotProps={{
                textField: {
                  label: 'To date',
                  size: 'small',
                  sx: { minWidth: 160, width: isMobile ? '100%' : 160 },
                  fullWidth: isMobile,
                },
              }}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
